import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  BuildingMaintenanceSettingsDetail,
  BuildingMaintenanceSettingsRequest,
  MaintenanceBuildingOption,
  MaintenanceCalendarEntry,
  MaintenanceCalendarFilters,
  MaintenanceCategoryListItem,
  MaintenanceCategoryOption,
  MaintenanceCategoryRequest,
  MaintenanceItemDetail,
  MaintenanceItemListItem,
  MaintenanceItemListQuery,
  MaintenanceItemRequest,
  MaintenanceRecordListItem,
  MaintenanceRecordListQuery,
  RecordMaintenanceRequest,
} from './buildingManagementTypes';

/**
 * Serializes a list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function serialize(params: URLSearchParams): string {
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function listQuery(query: ListQuery): string {
  return serialize(base(query, new URLSearchParams()));
}

function itemQuery(query: MaintenanceItemListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.category !== undefined) params.set('category', query.category);
  if (query.building !== undefined) params.set('building', query.building);
  if (query.aging !== undefined) params.set('aging', query.aging);
  if (query.recurrence !== undefined) params.set('recurrence', query.recurrence);

  return serialize(params);
}

/** The maintenance register, its records, its calendar and its settings row. */
export const maintenanceItemsApi = {
  list: (query: MaintenanceItemListQuery, signal?: AbortSignal) =>
    api<PagedResult<MaintenanceItemListItem>>(`/api/maintenance-items${itemQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<MaintenanceItemDetail>(`/api/maintenance-items/${id}`, { signal }),

  create: (body: MaintenanceItemRequest) =>
    api<MaintenanceItemDetail>('/api/maintenance-items', { method: 'POST', body }),

  update: (id: string, body: MaintenanceItemRequest) =>
    api<MaintenanceItemDetail>(`/api/maintenance-items/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`/api/maintenance-items/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/maintenance-items/${id}/restore`, { method: 'POST' }),

  /** The live buildings, for the form's picker — this module's own feed, on its write keys. */
  buildings: (signal?: AbortSignal) =>
    api<MaintenanceBuildingOption[]>('/api/maintenance-items/buildings', { signal }),

  /**
   * Every item falling due between two calendar dates, inclusive — at most a year. Takes the
   * register's filters, written on PRESENCE as the list's are; none at all is every live item.
   */
  calendar: (from: string, to: string, filters: MaintenanceCalendarFilters = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams({ from, to });

    if (filters.search?.trim()) params.set('search', filters.search.trim());
    if (filters.status !== undefined) params.set('status', filters.status);
    if (filters.category !== undefined) params.set('category', filters.category);
    if (filters.building !== undefined) params.set('building', filters.building);
    if (filters.aging !== undefined) params.set('aging', filters.aging);
    if (filters.recurrence !== undefined) params.set('recurrence', filters.recurrence);

    return api<MaintenanceCalendarEntry[]>(`/api/maintenance-items/calendar?${params.toString()}`, { signal });
  },

  records: (id: string, query: MaintenanceRecordListQuery, signal?: AbortSignal) => {
    const params = base(query, new URLSearchParams());
    if (query.type !== undefined) params.set('type', query.type);

    return api<PagedResult<MaintenanceRecordListItem>>(`/api/maintenance-items/${id}/records${serialize(params)}`, {
      signal,
    });
  },

  record: (id: string, body: RecordMaintenanceRequest) =>
    api<MaintenanceRecordListItem>(`/api/maintenance-items/${id}/records`, { method: 'POST', body }),

  removeRecord: (id: string, recordId: string) =>
    api<void>(`/api/maintenance-items/${id}/records/${recordId}`, { method: 'DELETE' }),

  restoreRecord: (id: string, recordId: string) =>
    api<void>(`/api/maintenance-items/${id}/records/${recordId}/restore`, { method: 'POST' }),

  /**
   * Files the renewed permit or service report against a record.
   *
   * Through `apiUpload` because the modal shows a REAL progress figure — `fetch` has no
   * upload-progress event.
   */
  attach: (id: string, recordId: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<MaintenanceRecordListItem>(`/api/maintenance-items/${id}/records/${recordId}/attachment`, form, {
      onProgress,
    });
  },

  /**
   * Fetches the filed document. A FETCH then an object URL, never an `<a href>` at the endpoint:
   * the token travels in a header no navigation sends, and the API has its own origin.
   */
  download: (id: string, recordId: string, signal?: AbortSignal) =>
    apiBlob(`/api/maintenance-items/${id}/records/${recordId}/attachment`, { signal }),

  settings: (signal?: AbortSignal) =>
    api<BuildingMaintenanceSettingsDetail>('/api/maintenance-items/settings', { signal }),

  updateSettings: (body: BuildingMaintenanceSettingsRequest) =>
    api<BuildingMaintenanceSettingsDetail>('/api/maintenance-items/settings', { method: 'PUT', body }),
};

/** What a maintenance item IS — the catalog behind Settings. */
export const maintenanceCategoriesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<MaintenanceCategoryListItem>>(`/api/maintenance-categories${listQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<MaintenanceCategoryListItem>(`/api/maintenance-categories/${id}`, { signal }),

  options: (signal?: AbortSignal) =>
    api<MaintenanceCategoryOption[]>('/api/maintenance-categories/options', { signal }),

  create: (body: MaintenanceCategoryRequest) =>
    api<MaintenanceCategoryListItem>('/api/maintenance-categories', { method: 'POST', body }),

  update: (id: string, body: MaintenanceCategoryRequest) =>
    api<MaintenanceCategoryListItem>(`/api/maintenance-categories/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`/api/maintenance-categories/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/maintenance-categories/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/maintenance-categories/clear-data', { method: 'POST' }),
};
